import { openDB, type IDBPDatabase } from 'idb';
import { validarVenta, type VentaEnCurso } from './venta-en-curso';

/**
 * Los pedidos abiertos de este equipo: ventas de clientes que siguen en el local, guardadas hasta cobrarlas.
 * Cada cambio va a localStorage al instante, y una copia en IndexedDB con escritura estricta respalda las dos.
 */

export interface Pedido {
  /** También es el `uuid_cliente` de la venta: cobrar dos veces el mismo pedido devuelve la misma venta. */
  id: string;
  numero: number;
  nombre: string | null;
  negocioId: string;
  /** El día en que se abrió; uno de otro día se marca, no se borra. */
  fecha: string;
  abiertoEn: number;
  guardadoEn: number;
  /** `cobrando` desde que se manda a cobrar hasta que la venta quedó registrada o en la cola. */
  estado: 'abierto' | 'cobrando';
  venta: VentaEnCurso;
}

const PREFIJO = 'tanteo-pedido:';
const clave = (negocioId: string, id: string) => `${PREFIJO}${negocioId}:${id}`;

/** Un pedido guardado que se entiende; null si no. Puede estar vacío si se acaba de crear. */
function interpretar(dato: unknown, negocioId: string): Pedido | null {
  const p = dato as Partial<Pedido> | null;
  if (!p || p.negocioId !== negocioId || typeof p.id !== 'string' || !Number.isInteger(p.numero)) return null;
  const vacio = Array.isArray(p.venta?.lineas) && p.venta.lineas.length === 0 && typeof p.venta.idVenta === 'string';
  const venta: VentaEnCurso | null = vacio
    ? { idVenta: p.venta!.idVenta, lineas: [], cliente: null, cuentaId: null, pagos: null, descuento: null }
    : validarVenta(p.venta);
  if (!venta) return null;
  return {
    id: p.id,
    numero: p.numero as number,
    nombre: p.nombre?.trim() || null,
    negocioId,
    fecha: String(p.fecha ?? ''),
    abiertoEn: Number(p.abiertoEn) || Date.now(),
    guardadoEn: Number(p.guardadoEn) || 0,
    estado: p.estado === 'cobrando' ? 'cobrando' : 'abierto',
    venta,
  };
}

function leerLocales(negocioId: string): Pedido[] {
  const pedidos: Pedido[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(`${PREFIJO}${negocioId}:`)) continue;
      const p = interpretar(JSON.parse(localStorage.getItem(k) ?? 'null'), negocioId);
      if (p) pedidos.push(p);
    }
  } catch {
    // Sin acceso a localStorage quedan los de IndexedDB.
  }
  return pedidos;
}

/** Cuántos pedidos abiertos hay en este equipo para el negocio, sin esperar a IndexedDB. */
export function contarPedidosLocales(negocioId: string): number {
  return leerLocales(negocioId).length;
}

let dbPromesa: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  dbPromesa ??= openDB('tanteo-pedidos', 1, {
    upgrade(base) {
      if (!base.objectStoreNames.contains('pedidos')) base.createObjectStore('pedidos', { keyPath: 'id' });
    },
  });
  return dbPromesa;
}

/** Los pedidos del negocio: de cada uno, la copia guardada más reciente entre localStorage y el disco. */
export async function listarPedidos(negocioId: string): Promise<Pedido[]> {
  const porId = new Map<string, Pedido>();
  for (const p of leerLocales(negocioId)) porId.set(p.id, p);
  try {
    for (const dato of await (await db()).getAll('pedidos')) {
      const p = interpretar(dato, negocioId);
      if (p && (porId.get(p.id)?.guardadoEn ?? -1) < p.guardadoEn) porId.set(p.id, p);
    }
  } catch {
    // Sin IndexedDB quedan los de localStorage.
  }
  return [...porId.values()].sort((a, b) => a.abiertoEn - b.abiertoEn);
}

/** El siguiente número del día en este equipo: vuelve a 1 cada día. */
export function siguienteNumero(pedidos: Pedido[], hoy: string): number {
  return pedidos.filter((p) => p.fecha === hoy).reduce((mayor, p) => Math.max(mayor, p.numero), 0) + 1;
}

export function crearPedido(
  negocioId: string,
  hoy: string,
  nombre: string | null,
  venta: VentaEnCurso,
  existentes: Pedido[]
): Pedido {
  const ahora = Date.now();
  return {
    id: venta.idVenta,
    numero: siguienteNumero(existentes, hoy),
    nombre: nombre?.trim() || null,
    negocioId,
    fecha: hoy,
    abiertoEn: ahora,
    guardadoEn: ahora,
    estado: 'abierto',
    venta,
  };
}

/** Escritura síncrona en localStorage; false si el navegador no la aceptó. */
export function guardarPedido(p: Pedido): boolean {
  try {
    localStorage.setItem(clave(p.negocioId, p.id), JSON.stringify(p));
    return true;
  } catch {
    return false;
  }
}

/** La copia en disco, con escritura estricta y releída para confirmar que quedó. */
export async function guardarPedidoEnDisco(p: Pedido): Promise<boolean> {
  try {
    const base = await db();
    const tx = base.transaction('pedidos', 'readwrite', { durability: 'strict' });
    await tx.store.put(p);
    await tx.done;
    const leido = (await base.get('pedidos', p.id)) as Pedido | undefined;
    return leido?.guardadoEn === p.guardadoEn;
  } catch {
    return false;
  }
}

export async function borrarPedido(negocioId: string, id: string): Promise<void> {
  try {
    localStorage.removeItem(clave(negocioId, id));
  } catch {
    // Sin localStorage no había copia ahí.
  }
  try {
    const tx = (await db()).transaction('pedidos', 'readwrite', { durability: 'strict' });
    await tx.store.delete(id);
    await tx.done;
  } catch {
    // Si el disco falla, la copia vieja reaparece y se puede cobrar: registrar_venta no la duplica.
  }
}

/**
 * Toma el pedido para esta pestaña; null si otra pestaña lo tiene abierto. Devuelve con qué soltarlo.
 * Sin Web Locks en el navegador no hay con qué coordinar, y se toma siempre.
 */
export async function tomarPedido(id: string): Promise<(() => void) | null> {
  if (typeof navigator === 'undefined' || !navigator.locks) return () => {};
  return new Promise((resolver) => {
    void navigator.locks.request(`tanteo-pedido-${id}`, { ifAvailable: true }, (candado) => {
      if (!candado) {
        resolver(null);
        return;
      }
      return new Promise<void>((soltar) => resolver(soltar));
    });
  });
}

/** Pide al navegador no borrar estos datos cuando le falte espacio. */
export function pedirPersistencia(): void {
  void navigator.storage?.persist?.().catch(() => {});
}
